/**
 * Una foto de paciente que se puede ACERCAR (dirección, 24/9/2026: «no haces
 * zoom, pero que permita hacer zoom para acercarnos y ver mejor la cara»).
 *
 *  · Se abre ENTERA, ajustada al hueco, sin zoom.
 *  · Móvil y tablet: pellizcando; y doble toque, a 2× donde se toca (otra
 *    vez, vuelve a 1×).
 *  · Ordenador: la rueda del ratón (o el pellizco del trackpad) acerca donde
 *    está el puntero; los botones − / +; doble clic, 2× y vuelta a 1×.
 *  · Acercada, se arrastra para moverse por ella, sin salirse de la foto.
 *  · «Tamaño normal» la devuelve a 1×. Con el teclado: + / − / 0.
 *  · Una pista discreta mientras está a 1×: «Pellizca o usa la rueda para
 *    acercar».
 *
 * Sin librerías: pointer events y un `transform` de CSS sobre la foto (el
 * navegador la vuelve a pintar con su resolución de verdad, por eso se guarda
 * entera). Lo que va encima de la foto (las marcas) va en `children` y se
 * mueve con ella; si `children` es una función, recibe la escala (para que un
 * punto no crezca al acercar).
 *
 * MODO DE PONER MARCAS: con `onToque({ x, y })` (en % de la foto), cada toque
 * o clic SIN arrastrar es una marca, y se pone AL MOMENTO. En este modo el
 * doble clic / doble toque NO acerca: son dos marcas (revisión 24/9/2026:
 * esperar 260 ms por si era un doble clic se comía la primera de dos marcas
 * rápidas, y un doble clic lento dejaba una marca suelta además de acercar).
 * Se acerca con la rueda, pellizcando, con − / + o con el teclado. Fuera de
 * ese modo (sin `onToque`), el doble clic / doble toque acerca como siempre.
 * Mientras la foto no ha cargado, los clics y toques no hacen nada (antes
 * ponían una marca en la esquina, en 0 %, 0 %).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, Minimize2, Loader2, ImageOff, RotateCcw } from 'lucide-react';

export const ESCALA_MAXIMA = 8;
const PASO_BOTON = 1.5;
const PASO_TECLA = 1.25;
const DOBLE_MS = 300;       // entre los dos toques de un doble toque
const DOBLE_PX = 30;        // y lo cerca que tienen que caer
const MOVER_PX = 6;         // lo que hay que mover para que no sea un toque

const acotarEscala = (s) => Math.min(ESCALA_MAXIMA, Math.max(1, s));

// Un punto de la pantalla, en píxeles del hueco de la foto.
function puntoEn(el, clientX, clientY) {
  const r = el?.getBoundingClientRect();
  return { x: clientX - (r?.left || 0), y: clientY - (r?.top || 0) };
}

function movimientoReducido() {
  try {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  } catch {
    return false;
  }
}

export default function FotoZoom({
  src,
  alt = '',
  onToque,
  children,
  claseImagen = 'max-h-[72vh]',
  testId = 'foto-zoom',
  pista = 'Pellizca o usa la rueda para acercar',
}) {
  const marcoRef = useRef(null);
  const escenaRef = useRef(null);
  const [vista, setVista] = useState({ s: 1, x: 0, y: 0 });
  const vistaRef = useRef(vista);
  const [animar, setAnimar] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [carga, setCarga] = useState('cargando'); // cargando | lista | error
  const [intento, setIntento] = useState(0);
  const punteros = useRef(new Map());
  const gesto = useRef(null);
  const toque = useRef({ t: 0, x: 0, y: 0 });
  const dobleAt = useRef(0);
  const onToqueRef = useRef(onToque);
  // Con error no hay hueco de la foto: al reintentar se monta otro y la
  // rueda se vuelve a enganchar a ese.
  const sinMarco = carga === 'error';
  // Lo lee también la rueda, que se engancha una sola vez.
  const lista = carga === 'lista';
  const listaRef = useRef(lista);
  listaRef.current = lista;

  useEffect(() => { onToqueRef.current = onToque; }, [onToque]);

  // Escala y desplazamiento dentro de lo posible: entre 1× y 8×, y sin que
  // asome el fondo por ningún lado (la foto siempre cubre el hueco).
  const acotar = useCallback(({ s, x, y }) => {
    const el = marcoRef.current;
    const w = el?.clientWidth || 0;
    const h = el?.clientHeight || 0;
    const e = acotarEscala(s);
    if (e <= 1.001) return { s: 1, x: 0, y: 0 };
    return {
      s: e,
      x: Math.min(0, Math.max(w - w * e, x)),
      y: Math.min(0, Math.max(h - h * e, y)),
    };
  }, []);

  const poner = useCallback((v, conAnimacion = false) => {
    const nueva = acotar(v);
    vistaRef.current = nueva;
    setAnimar(conAnimacion && !movimientoReducido());
    setVista(nueva);
  }, [acotar]);

  // Acercar (factor > 1) o alejar dejando QUIETO el punto (px, py) del hueco.
  const zoomEn = useCallback((factor, px, py, conAnimacion = false) => {
    const v = vistaRef.current;
    const s = acotarEscala(v.s * factor);
    const k = s / v.s;
    poner({ s, x: px - (px - v.x) * k, y: py - (py - v.y) * k }, conAnimacion);
  }, [poner]);

  const alCentro = (factor) => {
    const el = marcoRef.current;
    zoomEn(factor, (el?.clientWidth || 0) / 2, (el?.clientHeight || 0) / 2, true);
  };

  const normal = useCallback(() => poner({ s: 1, x: 0, y: 0 }, true), [poner]);

  // Doble toque / doble clic: a 2× donde se pulsa, o de vuelta a 1×.
  const alternar = useCallback((clientX, clientY) => {
    if (vistaRef.current.s > 1.01) {
      normal();
      return;
    }
    const p = puntoEn(marcoRef.current, clientX, clientY);
    zoomEn(2 / vistaRef.current.s, p.x, p.y, true);
  }, [normal, zoomEn]);

  // Foto nueva: entera otra vez.
  useEffect(() => {
    vistaRef.current = { s: 1, x: 0, y: 0 };
    setVista(vistaRef.current);
    setCarga('cargando');
    setIntento(0);
  }, [src]);

  // Si cambia el hueco (girar el móvil, la barra del navegador), la foto
  // acercada se recoloca sin salirse.
  useEffect(() => {
    const recolocar = () => poner(vistaRef.current);
    window.addEventListener('resize', recolocar);
    return () => window.removeEventListener('resize', recolocar);
  }, [poner]);

  // La rueda: con un oyente de verdad (no pasivo) para que no desplace la
  // página por debajo. El pellizco del trackpad llega como rueda + Ctrl.
  useEffect(() => {
    const el = marcoRef.current;
    if (!el) return undefined;
    const alRodar = (e) => {
      if (!listaRef.current) return;   // sin foto todavía, la página rueda como siempre
      e.preventDefault();
      const unidad = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
      const delta = (e.deltaY || 0) * unidad;
      if (!delta) return;
      const p = puntoEn(el, e.clientX, e.clientY);
      zoomEn(Math.exp(-delta * (e.ctrlKey ? 0.01 : 0.002)), p.x, p.y);
    };
    el.addEventListener('wheel', alRodar, { passive: false });
    return () => el.removeEventListener('wheel', alRodar);
  }, [zoomEn, sinMarco]);

  const soltarCaptura = (e) => {
    try { marcoRef.current?.releasePointerCapture?.(e.pointerId); } catch { /* ya suelto */ }
  };

  const alBajar = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (!listaRef.current) return;   // la foto aún no está: ni marca ni gesto
    try { marcoRef.current?.setPointerCapture?.(e.pointerId); } catch { /* sin captura */ }
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const v = vistaRef.current;
    if (punteros.current.size === 1) {
      gesto.current = { tipo: 'toque', x0: e.clientX, y0: e.clientY, v0: v, movido: false, pellizco: false };
    } else if (punteros.current.size === 2) {
      const [a, b] = [...punteros.current.values()];
      const medio = puntoEn(marcoRef.current, (a.x + b.x) / 2, (a.y + b.y) / 2);
      gesto.current = {
        tipo: 'pellizco',
        d0: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        medio0: medio,
        v0: v,
        pellizco: true,
      };
      setArrastrando(false);
    }
  };

  const alMover = (e) => {
    if (!punteros.current.has(e.pointerId)) return;
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gesto.current;
    if (!g) return;
    if (g.tipo === 'pellizco' && punteros.current.size >= 2) {
      const [a, b] = [...punteros.current.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const medio = puntoEn(marcoRef.current, (a.x + b.x) / 2, (a.y + b.y) / 2);
      const s = acotarEscala(g.v0.s * (d / g.d0));
      // El punto de la foto que estaba bajo los dedos sigue bajo los dedos.
      const cx = (g.medio0.x - g.v0.x) / g.v0.s;
      const cy = (g.medio0.y - g.v0.y) / g.v0.s;
      poner({ s, x: medio.x - cx * s, y: medio.y - cy * s });
      return;
    }
    if (g.tipo === 'toque' || g.tipo === 'arrastre') {
      const dx = e.clientX - g.x0;
      const dy = e.clientY - g.y0;
      if (!g.movido && Math.hypot(dx, dy) > MOVER_PX) {
        g.movido = true;
        g.tipo = 'arrastre';
        if (g.v0.s > 1) setArrastrando(true);
      }
      if (g.movido && g.v0.s > 1) poner({ s: g.v0.s, x: g.v0.x + dx, y: g.v0.y + dy });
    }
  };

  const alSubir = (e, cancelado = false) => {
    if (!punteros.current.has(e.pointerId)) return;
    punteros.current.delete(e.pointerId);
    soltarCaptura(e);
    const g = gesto.current;
    if (punteros.current.size === 1 && g?.tipo === 'pellizco') {
      // Queda un dedo: sigue arrastrando desde donde está, sin marcar nada.
      const [resto] = [...punteros.current.values()];
      gesto.current = { tipo: 'arrastre', x0: resto.x, y0: resto.y, v0: vistaRef.current, movido: true, pellizco: true };
      return;
    }
    if (punteros.current.size > 0) return;
    gesto.current = null;
    setArrastrando(false);
    if (cancelado || !g || g.tipo !== 'toque' || g.movido || g.pellizco) return;
    if (!listaRef.current) return;

    if (onToqueRef.current) {
      // Modo de poner marcas: el toque ES la marca, ya. Dónde, en % de la
      // foto (la escena ya lleva el zoom en su caja). Sin caja no hay foto
      // pintada, y una marca en 0 %, 0 % sería inventada.
      const r = escenaRef.current?.getBoundingClientRect();
      if (!r || !(r.width > 0) || !(r.height > 0)) return;
      onToqueRef.current({
        x: Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100)),
        y: Math.max(0, Math.min(100, ((e.clientY - r.top) / r.height) * 100)),
      });
      return;
    }

    // Solo para mirar: ¿es el segundo toque de un doble toque? Entonces acerca.
    const ahora = Date.now();
    const t = toque.current;
    if (ahora - t.t < DOBLE_MS && Math.hypot(e.clientX - t.x, e.clientY - t.y) < DOBLE_PX) {
      toque.current = { t: 0, x: 0, y: 0 };
      dobleAt.current = ahora;   // el dblclick que el navegador manda detrás, fuera
      alternar(e.clientX, e.clientY);
      return;
    }
    toque.current = { t: ahora, x: e.clientX, y: e.clientY };
  };

  // El doble clic del ratón (y el de algún navegador táctil). Si el doble
  // toque ya lo han contado los pointer events, no se cuenta dos veces. En el
  // modo de poner marcas no acerca: han sido dos marcas.
  const alDobleClic = (e) => {
    e.preventDefault();
    if (onToqueRef.current || !listaRef.current) return;
    if (Date.now() - dobleAt.current < 500) return;
    toque.current = { t: 0, x: 0, y: 0 };
    alternar(e.clientX, e.clientY);
  };

  const alTeclear = (e) => {
    if (e.key === '+' || e.key === '=') { e.preventDefault(); alCentro(PASO_TECLA); }
    else if (e.key === '-' || e.key === '_') { e.preventDefault(); alCentro(1 / PASO_TECLA); }
    else if (e.key === '0') { e.preventDefault(); normal(); }
  };

  const acercada = vista.s > 1.001;
  const porcentaje = Math.round(vista.s * 100);
  const cursor = arrastrando ? 'grabbing' : acercada ? 'grab' : onToque ? 'crosshair' : 'zoom-in';
  const conIntento = intento ? `${src}${src.includes('?') ? '&' : '?'}r=${intento}` : src;
  const boton = 'inline-flex items-center justify-center h-9 min-w-9 px-2 rounded-full text-white/90 hover:text-white hover:bg-white/15 disabled:opacity-40 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-white';

  return (
    <div
      className={`relative inline-block max-w-full align-middle ${carga !== 'lista' ? 'min-w-[200px] min-h-[200px]' : ''}`}
      data-testid={testId}
    >
      {carga === 'error' ? (
        <div role="alert" className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center text-sm text-white/85"
          data-testid={`${testId}-error`}>
          <ImageOff className="w-8 h-8 text-white/60" aria-hidden="true" />
          <div>No se ha podido cargar la foto. Comprueba la conexión.</div>
          <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-white/40 px-3 py-1.5 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            onClick={() => { setCarga('cargando'); setIntento((n) => n + 1); }}>
            <RotateCcw className="w-4 h-4" aria-hidden="true" /> Reintentar
          </button>
        </div>
      ) : (
        <>
          <div
            ref={marcoRef}
            className="relative overflow-hidden select-none outline-none focus-visible:ring-2 focus-visible:ring-white"
            style={{ touchAction: 'none', cursor }}
            tabIndex={0}
            role="group"
            aria-label={`Foto, al ${porcentaje} %. Más y menos para acercar y alejar; cero, tamaño normal.`}
            onPointerDown={alBajar}
            onPointerMove={alMover}
            onPointerUp={(e) => alSubir(e)}
            onPointerCancel={(e) => alSubir(e, true)}
            onDoubleClick={alDobleClic}
            onKeyDown={alTeclear}
            data-testid={`${testId}-marco`}
          >
            <div
              ref={escenaRef}
              className="relative"
              style={{
                transform: `translate(${vista.x}px, ${vista.y}px) scale(${vista.s})`,
                transformOrigin: '0 0',
                transition: animar ? 'transform 180ms ease-out' : 'none',
              }}
              data-escala={vista.s}
              data-testid={`${testId}-escena`}
            >
              <img
                key={conIntento}
                src={conIntento}
                alt={alt}
                draggable={false}
                onLoad={() => setCarga('lista')}
                onError={() => setCarga('error')}
                className={`block max-w-full ${claseImagen} w-auto h-auto pointer-events-none select-none`}
                data-testid={`${testId}-img`}
              />
              {carga === 'lista' && (typeof children === 'function' ? children(vista.s) : children)}
            </div>
          </div>

          {carga === 'cargando' && (
            <div className="absolute inset-0 flex items-center justify-center text-white/80 text-sm gap-2 pointer-events-none">
              <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Cargando la foto…
            </div>
          )}

          {carga === 'lista' && !acercada && pista && (
            <div className="pointer-events-none absolute top-2 left-1/2 -translate-x-1/2 max-w-[calc(100%-1rem)] truncate rounded-full bg-black/55 px-2.5 py-1 text-[11px] text-white/90"
              data-testid={`${testId}-pista`}>
              {pista}
            </div>
          )}

          {carga === 'lista' && (
            <div className="absolute bottom-2 right-2 flex items-center gap-0.5 rounded-full bg-black/60 p-0.5 text-white shadow"
              data-testid={`${testId}-controles`}>
              <button type="button" className={boton} onClick={() => alCentro(1 / PASO_BOTON)} disabled={!acercada}
                aria-label="Alejar" title="Alejar (−)" data-testid={`${testId}-alejar`}>
                <ZoomOut className="w-4 h-4" aria-hidden="true" />
              </button>
              <span className="min-w-[3.25rem] text-center text-xs tabular-nums" aria-live="polite"
                data-testid={`${testId}-porcentaje`}>
                {porcentaje} %
              </span>
              <button type="button" className={boton} onClick={() => alCentro(PASO_BOTON)}
                disabled={vista.s >= ESCALA_MAXIMA - 0.001}
                aria-label="Acercar" title="Acercar (+)" data-testid={`${testId}-acercar`}>
                <ZoomIn className="w-4 h-4" aria-hidden="true" />
              </button>
              {acercada && (
                <button type="button" className={`${boton} gap-1 text-xs`} onClick={normal}
                  aria-label="Tamaño normal" title="Tamaño normal (0)" data-testid={`${testId}-normal`}>
                  <Minimize2 className="w-4 h-4" aria-hidden="true" />
                  <span className="hidden sm:inline">Tamaño normal</span>
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
